import { Box } from "@mui/material";
import { chartColors } from "@/theme/palette";

interface Props {
  values: number[];
  width?: number | string;
  height?: number;
  negative?: boolean;
  /**
   * Optional baseline value (in the same units as `values`). When
   * supplied, the line is rendered against a horizontal reference at
   * this value, with green shading above the baseline ("in the money")
   * and red shading below ("underwater"). Sergey 2026-05-01 — used in
   * the bot drawer so a lifetime TWR curve shows the underwater
   * region in red even when the line is currently above zero. Pass
   * `0` for TWR% curves, `allocatedAmount` for $ equity curves.
   */
  baseline?: number;
}

/**
 * Lightweight SVG sparkline (no chart lib needed).
 *
 * Two render modes:
 *  - Default: single colour fill from the line down. Mint green when
 *    trending up overall (last ≥ first), coral red when trending down.
 *  - Baseline mode (when `baseline` is supplied): two clipped fills,
 *    green above the baseline and red below, with a dashed horizontal
 *    reference line at the baseline. The line itself stays one
 *    neutral colour so the area shading carries the story.
 */
export function Sparkline({ values, width = "100%", height = 40, negative, baseline }: Props) {
  if (!values || values.length < 2) {
    return <Box sx={{ width, height }} />;
  }

  const first = values[0]!;
  const last  = values[values.length - 1]!;
  const up    = negative ?? (last >= first);

  // Y-axis scaling — include the baseline in min/max so the reference
  // line is always inside the plotted region (otherwise an entirely-
  // above-baseline series clips the baseline at the bottom edge and a
  // viewer can't tell the underwater region is empty vs cropped).
  const lo = Math.min(...values, baseline ?? Infinity);
  const hi = Math.max(...values, baseline ?? -Infinity);
  const min = Number.isFinite(lo) ? lo : Math.min(...values);
  const max = Number.isFinite(hi) ? hi : Math.max(...values);
  const range = max - min || 1;
  const pad = 2;
  const vbW = 100;
  const vbH = 40;
  const innerW = vbW - pad * 2;
  const innerH = vbH - pad * 2;

  const yOf = (v: number) => pad + innerH - ((v - min) / range) * innerH;

  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * innerW;
    const y = yOf(v);
    return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");

  // Single-colour fallback path: line + close down to bottom.
  const fillSimple = `${pts} L ${pad + innerW},${pad + innerH} L ${pad},${pad + innerH} Z`;

  const stroke = baseline !== undefined
    // Neutral stroke in baseline mode — colour comes from the area
    // fills above/below the reference, not the line itself.
    ? "rgba(180,200,230,0.85)"
    : up ? chartColors.primary : chartColors.negative;

  // Stable ids (so multiple sparklines on a page don't collide on
  // gradient definitions). Length-based hash is good enough.
  const idTag = `${values.length}-${(baseline ?? "x")}`;
  const greenGradId = `spark-g-up-${idTag}`;
  const redGradId   = `spark-g-dn-${idTag}`;
  const simpleGradId = `spark-g-${up ? "up" : "dn"}-${idTag}`;
  const simpleFillColor = up ? "rgba(38,211,154," : "rgba(255,91,91,";

  // Baseline mode: build two filled regions — one for the area
  // between the line and the baseline above (in-the-money), one for
  // the area between the line and the baseline below (underwater).
  // Each is the same closed shape (line trace closed back along the
  // baseline at the same start/end x), clipped to the respective
  // half-plane so only the relevant pixels render.
  let baselineY = 0;
  let fillBothPath = "";
  if (baseline !== undefined) {
    baselineY = yOf(baseline);
    fillBothPath = `${pts} L ${pad + innerW},${baselineY.toFixed(2)} L ${pad},${baselineY.toFixed(2)} Z`;
  }

  return (
    <Box component="svg" viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="none"
         sx={{ width, height, display: "block" }}>
      <defs>
        {baseline !== undefined ? (
          <>
            <clipPath id={`clip-up-${idTag}`}>
              <rect x="0" y="0" width={vbW} height={baselineY} />
            </clipPath>
            <clipPath id={`clip-dn-${idTag}`}>
              <rect x="0" y={baselineY} width={vbW} height={vbH - baselineY} />
            </clipPath>
            <linearGradient id={greenGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="rgba(38,211,154,0.45)" />
              <stop offset="100%" stopColor="rgba(38,211,154,0.0)" />
            </linearGradient>
            <linearGradient id={redGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="rgba(255,91,91,0.0)" />
              <stop offset="100%" stopColor="rgba(255,91,91,0.45)" />
            </linearGradient>
          </>
        ) : (
          <linearGradient id={simpleGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={`${simpleFillColor}0.35)`} />
            <stop offset="100%" stopColor={`${simpleFillColor}0.0)`} />
          </linearGradient>
        )}
      </defs>
      {baseline !== undefined ? (
        <>
          {/* Green area above baseline */}
          <path d={fillBothPath} fill={`url(#${greenGradId})`} stroke="none"
                clipPath={`url(#clip-up-${idTag})`} />
          {/* Red area below baseline */}
          <path d={fillBothPath} fill={`url(#${redGradId})`} stroke="none"
                clipPath={`url(#clip-dn-${idTag})`} />
          {/* Dashed baseline reference */}
          <line x1={pad} y1={baselineY} x2={pad + innerW} y2={baselineY}
                stroke="rgba(180,200,230,0.5)" strokeWidth="0.5"
                strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
        </>
      ) : (
        <path d={fillSimple} fill={`url(#${simpleGradId})`} stroke="none" />
      )}
      <path d={pts} fill="none" stroke={stroke} strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </Box>
  );
}
